import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  appUpdateUrl,
  blockUser,
  createRoom,
  currentAppBuild,
  erizoUrl,
  fetchAppVersion,
  fetchConfig,
  fetchRooms,
  joinRoom,
  leaveRoom,
  openMeetingSession,
  signallingHost,
  updateRoom,
} from "./api.js";
import {
  discardLaunchFragment,
  fetchMe,
  logout,
  postToNativeHost,
  rememberSession,
  signInFromLaunch,
  signInWithDecent,
} from "./auth.js";
import { HostConsole } from "./HostConsole.jsx";
import { AccountProfile } from "./AccountProfile.jsx";
import { MeetingRoom } from "./MeetingRoom.jsx";
import { RoomList } from "./RoomList.jsx";
import { followAuthIdentity } from "./in-app-identity.js";
import { ensureMeetStyles } from "./meet-styles.js";
import { unlockAudio } from "./room-client.js";

function fromQuery() {
  if (typeof window === "undefined") return {};
  const q = new URLSearchParams(window.location.search);
  const session = q.get("session") || undefined;
  if (session) {
    rememberSession(session);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("session");
      window.history.replaceState({}, "", `${u.pathname}${u.search}${u.hash}`);
    } catch {
      /* ignore */
    }
  }
  return {
    roomId: q.get("id") || undefined,
    roomName: q.get("name") || undefined,
    token: q.get("token") || undefined,
    username: q.get("username") || q.get("walletAddress") || undefined,
    role: q.get("role") || "presenter",
    video: q.get("video") === "true",
    inApp: q.get("client") === "beagle-app",
    resume: q.get("resume") === "1",
  };
}

function normalizeIdentity(raw) {
  if (!raw) return null;
  const userId = raw.userId || raw.userid || "";
  if (!userId) return null;
  const name = raw.name && raw.name !== userId ? raw.name : "";
  return {
    userId,
    userid: userId,
    name: name || userId,
    avatarUrl: raw.avatarUrl || raw.avatar || "",
    punkId: raw.punkId ?? null,
    address: raw.address || "",
    via: raw.via === "web" || raw.via === "cli" || raw.via === "wallet" || raw.via === "launch" ? raw.via : "",
  };
}

function keepProfile(next, prev) {
  if (!next) return prev;
  if (!prev || prev.userId !== next.userId) return next;
  const nextName = next.name && next.name !== next.userId ? next.name : "";
  const prevName = prev.name && prev.name !== prev.userId ? prev.name : "";
  return {
    ...next,
    name: nextName || prevName || next.userId,
    avatarUrl: next.avatarUrl || prev.avatarUrl,
    punkId: next.punkId ?? prev.punkId,
    address: next.address || prev.address || "",
    via: next.via || prev.via || "",
  };
}

export function MeetingApp({
  apiBase = "",
  erizoSrc: erizoOverride,
  userId,
  name,
  avatarUrl,
  punkId,
  identity: identityProp,
  canCreate: canCreateProp,
  onExit,
}) {
  ensureMeetStyles();
  const boot = useMemo(() => fromQuery(), []);
  const hostIdentity = useMemo(
    () => normalizeIdentity(identityProp || { userId, name, avatarUrl, punkId }),
    [identityProp, userId, name, avatarUrl, punkId]
  );
  const src = erizoUrl(apiBase, erizoOverride);

  const [session, setSession] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joiningId, setJoiningId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [signingIn, setSigningIn] = useState(false);
  const [authHint, setAuthHint] = useState("");
  const [signInTick, setSignInTick] = useState(0);
  const [serverCanCreate, setServerCanCreate] = useState(false);
  const [serverOperator, setServerOperator] = useState(false);
  const [signalling, setSignalling] = useState("");
  const [call, setCall] = useState(null);
  const [hostOpen, setHostOpen] = useState(false);
  const [hostRoomId, setHostRoomId] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const [hungUp, setHungUp] = useState(false);
  const tokenBooted = useRef(false);
  const skipListLoad = useRef(false);

  const identity = useMemo(
    () => (session ? { ...(hostIdentity || {}), ...session } : null),
    [session, hostIdentity]
  );
  const canCreate = canCreateProp ?? serverCanCreate;
  // A create gate lets someone make rooms; only the allowlist runs the host console and edits every room.
  const operator = canCreateProp ?? serverOperator;
  const inCallBoot = !!(boot.roomId && (boot.token || boot.inApp || boot.resume));

  const load = useCallback(async () => {
    if (inCallBoot) return;
    setLoading(true);
    setError(null);
    try {
      // Apps-tab launch is only a way in when this tab has no session.
      // If they are already signed in, strip the fragment so a copied URL
      // carries no credential, but do not spend the assertion or switch who
      // they are.
      const meNow = await fetchMe(apiBase);
      if (meNow?.authenticated) {
        discardLaunchFragment();
      } else {
        try { await signInFromLaunch(apiBase); }
        catch (err) { setAuthHint(err instanceof Error ? err.message : String(err)); }
      }

      const [me, cfg, list] = await Promise.all([
        meNow?.authenticated ? Promise.resolve(meNow) : fetchMe(apiBase),
        fetchConfig(apiBase),
        fetchRooms(apiBase),
      ]);
      setSignalling(signallingHost(apiBase, cfg.signalling || cfg.licode));
      setRooms(list);
      if (me?.authenticated) {
        setSession((prev) => keepProfile(normalizeIdentity(me), prev));
        if (canCreateProp === undefined) {
          setServerCanCreate(!!me.canCreate);
          setServerOperator(!!(me.operator ?? me.canCreate));
        }
      } else {
        setSession(null);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load rooms";
      if (msg.includes("Sign in")) {
        setRooms([]);
        setAuthHint(msg);
      } else {
        setError(msg);
        setRooms([]);
      }
    } finally {
      setLoading(false);
    }
  }, [apiBase, canCreateProp, inCallBoot]);

  useEffect(() => {
    if (inCallBoot) {
      if (tokenBooted.current) return;
      tokenBooted.current = true;
      const ident = normalizeIdentity({
        userId: boot.username || "webview",
        name: boot.username || "Guest",
      }) || {
        userId: boot.username || "webview",
        userid: boot.username || "webview",
        name: boot.username || "Guest",
        avatarUrl: "",
        punkId: null,
        address: "",
        via: "wallet",
      };
      setLoading(false);
      let cancelled = false;
      const openWithToken = () => {
        if (!boot.token || boot.resume) return;
        setCall({
          room: { _id: boot.roomId, name: boot.roomName || "Meeting" },
          token: boot.token,
          skipMic: boot.role === "viewer",
          requireMic: boot.role !== "viewer",
        });
      };
      if (boot.resume) {
        (async () => {
          try {
            const me = await fetchMe(apiBase);
            if (cancelled) return;
            if (!me?.authenticated) throw new Error("Sign in again to rejoin after update");
            const nextIdentity = keepProfile(normalizeIdentity(me), ident) || ident;
            setSession(nextIdentity);
            const data = await joinRoom(apiBase, boot.roomId, boot.role || "presenter");
            if (cancelled) return;
            const presence = await openMeetingSession(apiBase, boot.roomId, { reconnect: true }).catch(() => null);
            const requireMic = data.requireMic !== false;
            setCall({
              room: {
                _id: boot.roomId,
                name: boot.roomName || "Meeting",
                requireMic,
                description: data.description || "",
                icon: data.icon || "",
                owner: data.owner || null,
                isOwner: !!data.isOwner,
              },
              token: data.token,
              skipMic: boot.role === "viewer",
              requireMic,
              presence,
            });
          } catch (err) {
            if (!cancelled) {
              setError(err instanceof Error ? err.message : "Could not rejoin after update");
            }
          }
        })();
        return () => {
          cancelled = true;
        };
      }
      // Publish only after the stored profile is known. A late reply still
      // updates the tiles and the streams that already went out.
      const stop = followAuthIdentity(() => fetchMe(apiBase), ident, {
        onIdentity: (next, phase) => {
          if (cancelled) return;
          const profile = keepProfile(normalizeIdentity(next), ident) || ident;
          setSession(profile);
          if (phase !== "late") openWithToken();
        },
      });
      return () => {
        cancelled = true;
        stop();
      };
    }
    if (!call && !skipListLoad.current) load();
  }, [load, call, inCallBoot, boot.token, boot.resume, boot.roomId, boot.roomName, boot.username, boot.role, apiBase]);

  useEffect(() => {
    let stopped = false;
    let checking = false;
    const check = async () => {
      if (stopped || checking) return;
      checking = true;
      try {
        const current = currentAppBuild();
        if (!/^index-[A-Za-z0-9_-]+\.js$/.test(current)) return;
        const next = await fetchAppVersion(apiBase);
        if (stopped || !next || next === current) return;
        const activeRoom = call?.room?._id
          ? { roomId: call.room._id, roomName: call.room.name, role: "presenter" }
          : inCallBoot
            ? { roomId: boot.roomId, roomName: boot.roomName, role: boot.role }
            : null;
        console.info(`[beagle-meet] update ${current} -> ${next}; reloading`);
        window.location.replace(appUpdateUrl(window.location.href, next, activeRoom));
      } catch {
        // An update check is advisory; network failure must not disturb a call.
      } finally {
        checking = false;
      }
    };
    const first = window.setTimeout(check, 15000);
    const timer = window.setInterval(check, 30000);
    const visible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("online", check);
    return () => {
      stopped = true;
      window.clearTimeout(first);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("online", check);
    };
  }, [apiBase, call, inCallBoot, boot.roomId, boot.roomName, boot.role]);

  const requestSignIn = () => setSignInTick((n) => n + 1);

  const handleSignIn = async (source) => {
    if (source !== "web" && source !== "cli") {
      requestSignIn();
      return;
    }
    setSigningIn(true);
    setAuthHint("");
    try {
      const d = await signInWithDecent(apiBase, source);
      setSession((prev) => keepProfile(normalizeIdentity(d), prev));
      if (canCreateProp === undefined) {
        setServerCanCreate(!!d.canCreate);
        setServerOperator(!!(d.operator ?? d.canCreate));
      }
      setAuthHint("");
      await load();
    } catch (err) {
      setAuthHint(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    await logout(apiBase);
    setSession(null);
    setRooms([]);
  };

  const handleJoin = async (room) => {
    unlockAudio();
    if (!identity) {
      requestSignIn();
      return;
    }
    setJoiningId(room._id);
    setError(null);
    try {
      const data = await joinRoom(apiBase, room._id, "presenter");
      const presence = await openMeetingSession(apiBase, room._id).catch(() => null);
      const requireMic = data.requireMic !== false;
      setCall({
        room: {
          ...room,
          requireMic,
          description: data.description || room.description,
          icon: data.icon || room.icon,
          owner: data.owner || room.owner,
          isOwner: !!data.isOwner,
        },
        token: data.token,
        skipMic: !requireMic && !!room._skipMic,
        requireMic,
        presence,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to join";
      if (msg.includes("Sign in")) {
        setAuthHint(msg);
        requestSignIn();
      } else setError(msg);
    } finally {
      setJoiningId(null);
    }
  };

  const handleCreate = async (spec) => {
    if (!identity) {
      requestSignIn();
      return;
    }
    const name = typeof spec === "string" ? spec : spec?.name;
    await createRoom(apiBase, name, typeof spec === "string" ? {} : spec);
    await load();
  };

  const handleUpdate = async (room, spec) => {
    await updateRoom(apiBase, room._id, spec);
    await load();
  };

  const leave = async () => {
    const roomId = call?.room?._id;
    postToNativeHost({ type: "beagle-meet-hangup" });
    skipListLoad.current = true;
    setCall(null);
    if (roomId) await leaveRoom(apiBase, roomId);
    skipListLoad.current = false;
    if (boot.inApp) {
      setHungUp(true);
      return;
    }
    if (boot.resume) {
      const u = new URL(window.location.href);
      for (const key of ["id", "name", "role", "resume", "_meetv"]) u.searchParams.delete(key);
      window.location.replace(`${u.pathname}${u.search}${u.hash}`);
      return;
    }
    onExit?.();
    if (boot.token) {
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("token");
        window.history.replaceState({}, "", `${u.pathname}${u.search}${u.hash}`);
      } catch {
        /* ignore */
      }
      return;
    }
    load();
  };

  const ownedRooms = rooms.filter((r) => r.isOwner);
  const canHost = !!identity && (operator || ownedRooms.length > 0);

  if (call && identity) {
    return (
      <div className="bm-root">
        <MeetingRoom
          token={call.token}
          identity={identity}
          roomName={call.room.name || boot.roomName}
          roomId={call.room._id}
          erizoSrc={src}
          apiBase={apiBase}
          inApp={boot.inApp}
          skipMic={!!call.skipMic}
          requireMic={call.requireMic !== false}
          isOwner={!!call.room.isOwner}
          initialPresence={call.presence || null}
          onExit={leave}
        />
      </div>
    );
  }

  if (inCallBoot) {
    return (
      <div className="bm-root" style={{ background: "#111" }}>
        {hungUp ? null : error ? (
          <div className="bm-status">Updated, but could not rejoin: {error}</div>
        ) : (
          <div className="bm-status">
            <div className="bm-spin" />
            <div>Joining {boot.roomName || "room"}…</div>
          </div>
        )}
      </div>
    );
  }

  if (accountOpen && identity) {
    return (
      <div className="bm-root">
        <AccountProfile
          apiBase={apiBase}
          identity={identity}
          canCreate={!!canCreate}
          canHost={canHost}
          joiningId={joiningId}
          onCreate={handleCreate}
          onJoin={handleJoin}
          onUpdate={handleUpdate}
          onClose={() => setAccountOpen(false)}
          onOpenHost={() => {
            setAccountOpen(false);
            setHostRoomId("");
            setHostOpen(true);
          }}
          onOpenRoomLog={(room) => {
            setAccountOpen(false);
            setHostRoomId(room._id || room.id || "");
            setHostOpen(true);
          }}
          onSignOut={handleSignOut}
        />
      </div>
    );
  }

  if (hostOpen && identity) {
    return (
      <div className="bm-root">
        <HostConsole
          apiBase={apiBase}
          rooms={operator ? rooms : ownedRooms}
          roomId={hostRoomId}
          canOperate={!!operator}
          onClose={() => {
            setHostOpen(false);
            setHostRoomId("");
          }}
        />
      </div>
    );
  }

  return (
    <div className="bm-root">
      <RoomList
        rooms={rooms}
        loading={loading}
        error={error}
        canCreate={!!identity && operator}
        joiningId={joiningId}
        search={search}
        onSearch={setSearch}
        filter={filter}
        onFilter={setFilter}
        signalling={signalling || "meet"}
        onJoin={handleJoin}
        onUpdate={handleUpdate}
        identity={identity}
        signingIn={signingIn}
        authHint={authHint}
        openSignIn={signInTick}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
        canHost={canHost}
        onOpenAccount={() => setAccountOpen(true)}
        onOpenHost={() => {
          setHostRoomId("");
          setHostOpen(true);
        }}
        onOpenRoomLog={(room) => {
          setHostRoomId(room._id);
          setHostOpen(true);
        }}
      />
    </div>
  );
}
